import React, { useState, useEffect } from 'react';
import { FaExchangeAlt, FaFilter, FaTimes, FaHome, FaUser } from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import SharedFilter from '../components/SharedFilter';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import bookingService from '../services/bookingService';
import { Project, projectService } from '../services/projectService';
import { formatDate } from '../utils/formatters';
import { formatMainDBAmount } from '../utils/formatAmount';
import '../css/pages/CancelledBookings.css';

interface TransferredBooking {
  id: number;
  bookingId: string;
  customerName: string;
  project: {
    id: number;
    name: string;
  };
  transferType: 'unit' | 'name' | 'unknown';
  fromUnit: string;
  toUnit: string;
  oldAmounts: {
    saleDeedAmount: number;
  };
  newAmounts: {
    saleDeedAmount: number;
  };
  transferReason: string;
  transferDate: string;
  transferredBy: string;
  requiresAuthorization: boolean;
  currentStatus: string;
}

const TransferredBookings: React.FC = () => {
  usePageTitle();
  const { user } = useAuth();
  const [transfers, setTransfers] = useState<TransferredBooking[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    project: '',
    search: '',
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      loadTransfers();
    }
  }, [projects, currentPage, appliedFilters, limit]);

  const loadInitialData = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
      // Set default project from user's first project
      if (user?.userProjects?.length) {
        const defaultProjectId = user.userProjects[0].projectId.toString();
        setFilterProject(defaultProjectId);
        setAppliedFilters(prev => ({ ...prev, project: defaultProjectId }));
      }
    } catch (err) {
      setError('Failed to load initial data');
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      project: filterProject,
      search: filterSearch,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setFilterProject('');
    setFilterSearch('');
    setAppliedFilters({
      project: '',
      search: '',
    });
    setCurrentPage(1);
  };

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getTransferredBookings({
        page: currentPage,
        limit: limit,
        search: appliedFilters.search || undefined,
        projectId: appliedFilters.project || undefined,
      });
      setTransfers(data.transfers);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.total);

      console.log('📊 Transferred bookings loaded:', {
        total: data.pagination.total,
        page: data.pagination.page,
        totalPages: data.pagination.totalPages,
        transfersCount: data.transfers.length,
      });
    } catch (err) {
      setError('Failed to load transferred bookings');
      console.error('❌ Error loading transferred bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);

    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const getTransferTypeBadge = (type: string) => {
    if (type === 'unit') {
      return (
        <span style={{
          padding: '4px 10px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          background: '#dbeafe',
          color: '#1e40af',
        }}>
          <FaHome style={{ marginRight: '4px', fontSize: '10px' }} />
          Unit Transfer
        </span>
      );
    } else if (type === 'name') {
      return (
        <span style={{
          padding: '4px 10px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          background: '#fce7f3',
          color: '#9f1239',
        }}>
          <FaUser style={{ marginRight: '4px', fontSize: '10px' }} />
          Name Transfer
        </span>
      );
    }
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        background: '#f3f4f6',
        color: '#6b7280',
      }}>
        Unknown
      </span>
    );
  };

  return (
    <div className="page-container">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')} className="error-close">
            ×
          </button>
        </div>
      )}

      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Project:</span>
            <select
              value={filterProject}
              onChange={(e) => {
                setFilterProject(e.target.value);
                setAppliedFilters(prev => ({ ...prev, project: e.target.value }));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 10px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Show:</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 10px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ←
            </button>

            {generatePageNumbers().map((pageNum) => (
              <button
                key={pageNum}
                className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              →
            </button>
          </div>
        )}
      </CompactActionBar>

      {/* Filters Panel - Using SharedFilter Component */}
      {showFilters && (
        <SharedFilter
          customerName={filterSearch}
          onCustomerNameChange={setFilterSearch}
          customerNamePlaceholder="Customer, unit (old/new), booking ID..."
          onApply={() => {
            handleApplyFilters();
          }}
          onClear={clearAllFilters}
        />
      )}

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          <div style={{ fontSize: '16px' }}>Loading transferred bookings...</div>
        </div>
      ) : transfers.length === 0 ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center', borderRadius: '8px', margin: '20px' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <FaExchangeAlt style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '20px' }} />
            <h2 style={{ fontSize: '20px', color: '#1f2937', marginBottom: '12px' }}>No Transfers Found</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              No booking transfers have been recorded yet.
            </p>
            <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '6px', marginTop: '24px' }}>
              <p style={{ fontWeight: 600, marginBottom: '8px', color: '#1f2937' }}>How to transfer a booking:</p>
              <p style={{ color: '#6b7280' }}>Go to any booking's edit page and click the "Transfer Booking" button.</p>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Sr. No</th>
                <th style={{ width: '200px' }}>Customer</th>
                <th style={{ width: '150px' }}>Project</th>
                <th style={{ width: '120px' }}>Type</th>
                <th style={{ width: '120px' }}>From Unit</th>
                <th style={{ width: '120px' }}>To Unit</th>
                <th style={{ width: '140px', textAlign: 'right' }}>Old Amount</th>
                <th style={{ width: '140px', textAlign: 'right' }}>New Amount</th>
                <th style={{ width: '200px' }}>Reason</th>
                <th style={{ width: '120px' }}>Transferred By</th>
                <th style={{ width: '120px' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((transfer, index) => (
                <tr key={transfer.id}>
                  <td>
                    <span style={{ fontWeight: 600, color: '#4f46e5' }}>
                      {totalRecords - ((currentPage - 1) * limit) - index}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#1f2937' }}>
                      {transfer.customerName}
                    </div>
                  </td>
                  <td>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>
                      {transfer.project?.name || 'N/A'}
                    </span>
                  </td>
                  <td>{getTransferTypeBadge(transfer.transferType)}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      background: '#fef3c7',
                      color: '#92400e',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}>
                      {transfer.fromUnit}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      background: '#d1fae5',
                      color: '#065f46',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}>
                      {transfer.toUnit}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ color: '#dc2626', fontSize: '13px' }}>
                      {formatMainDBAmount(transfer.oldAmounts.saleDeedAmount)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ color: '#059669', fontWeight: 600, fontSize: '13px' }}>
                      {formatMainDBAmount(transfer.newAmounts.saleDeedAmount)}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {transfer.transferReason || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {transfer.transferredBy}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {formatDate(transfer.transferDate)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransferredBookings;
